import { useCallback, useState } from "react";
import { toast } from "sonner";
import { useSWRConfig } from "swr";
import { AgentRun } from "@/models/agent/agentRun";
import { AgentService } from "@/services/agent/agent.service";

export const useAgentRunMutations = (threadId: string | null) => {
  const { mutate } = useSWRConfig();
  const [isCancelling, setIsCancelling] = useState(false);

  // 楽観的更新: SSEイベントで受け取ったAgentRunをキャッシュに反映
  const optimisticUpdate = useCallback((agentRun: AgentRun) => {
    mutate(
      (key: unknown) => Array.isArray(key) && key[0] === "agent" && key[1] === "agentRuns" && key[2] === threadId,
      (current: AgentRun[] | undefined) => {
        if (!current) return [agentRun];

        const existingIndex = current.findIndex((run) => run.id === agentRun.id);
        if (existingIndex >= 0) {
          const updated = [...current];
          updated[existingIndex] = agentRun;
          return updated;
        }
        return [agentRun, ...current];
      },
      { revalidate: false },
    );
  }, [mutate, threadId]);

  // optimisticなrunを実runで置き換える: APIレスポンス後に呼び出す
  const replaceOptimisticRun = useCallback((optimisticId: string, agentRun: AgentRun) => {
    mutate(
      (key: unknown) => Array.isArray(key) && key[0] === "agent" && key[1] === "agentRuns" && key[2] === threadId,
      (current: AgentRun[] | undefined) => {
        const filtered = (current ?? []).filter((run) => run.id !== optimisticId);
        const existingIndex = filtered.findIndex((run) => run.id === agentRun.id);
        if (existingIndex >= 0) {
          const updated = [...filtered];
          updated[existingIndex] = agentRun;
          return updated;
        }
        return [agentRun, ...filtered];
      },
      { revalidate: false },
    );
  }, [mutate, threadId]);

  const cancelAgent = async (agentRunId: string) => {
    setIsCancelling(true);
    try {
      await AgentService.cancelAgent({ agent_run_id: agentRunId });
      toast.success("エージェント実行をキャンセルしました");
    } catch (error) {
      console.error("Failed to cancel agent:", error);
      toast.error("キャンセルに失敗しました。もう一度お試しください");
      throw error;
    } finally {
      setIsCancelling(false);
    }
  };

  return {
    optimisticUpdate,
    replaceOptimisticRun,
    cancelAgent,
    isCancelling,
  };
};
